import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import { Col, Container, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import PerformanceSummaryView from "./PerformanceSummaryView";
import { loadReportsHelper } from "helpers/loadReports";
import LocationsPerformanceTable from "./LocationsPerformanceTable";
import { getLaborReports } from "helpers/fakebackend_helper";
import { FILES_URL } from "helpers/url_helper";
import formatCurrency from "constants/formatCurrency";
import MonthlyPerformanceChart from "./MonthlyPerformanceChart";
import EmployeesPerformanceTable from "./EmployeesPerformanceTable";
import LeaseSettlementsPerformanceTable from "./LeaseSettlementsPerformanceTable";

const fmt = (d) => d.toISOString().slice(0, 10);

const Performance = (props) => {//
  document.title = "Performance | CurbDash";

  const user = JSON.parse(localStorage.getItem("authUser"));

  // --- STATE MANAGEMENT ---
  const defaults = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: fmt(start), end: fmt(end) };
  }, []);

  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState("All Managers");
  const [selectedStructure, setSelectedStructure] = useState("All Types");
  const [progressMessages, setProgressMessages] = useState([]);
  const [included, setIncluded] = useState(["resolve"]);
  const [filterBySource, setFilterBySource] = useState(false);
  const [topHoursEmployee, setTopHoursEmployee] = useState(null);
  const [topEarningEmployee, setTopEarningEmployee] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);

  const fetchAndCalculateTopEmployees = async (reportsObj, start, end) => {
    // 1. Extract Location IDs from the loaded reports
    const locationIds = Object.values(reportsObj).map((r) => r.id).filter(id => id);

    if (locationIds.length === 0) {
      setTopHoursEmployee(null);
      setTopEarningEmployee(null);
      setAllEmployees([]);
      return;
    }

    try {
      // 2. Fetch Labor Report
      const res = await getLaborReports(user.token, {
        location_ids: locationIds,
        start_date: start,
        end_date: end,
      });

      const laborLocations = res.data || [];
      const employeeAggregates = {};
      const processedShiftIds = new Set(); // Track IDs to prevent double counting

      // 3. Aggregate data per employee (across all locations)
      laborLocations.forEach((loc) => {
        if (loc.employees) {
          loc.employees.forEach((emp) => {
            if (!employeeAggregates[emp.id]) {
              employeeAggregates[emp.id] = {
                ...emp,
                shifts: [], // Accumulate all unique shifts here
                totalNetHours: 0,
                totalEarnings: 0,
                locationNames: new Set(),
              };
            }

            // Capture the location name
            if (loc.name) {
              employeeAggregates[emp.id].locationNames.add(loc.name);
            }

            if (emp.shifts) {
              emp.shifts.forEach((shift) => {
                // If we have already calculated this specific shift ID, skip it
                if (processedShiftIds.has(shift.id)) return;

                // Mark this shift as processed
                processedShiftIds.add(shift.id);

                // Add shift to the employee's master list
                employeeAggregates[emp.id].shifts.push(shift);

                const hours = parseFloat(shift.net_hours) || 0;
                const rate = parseFloat(shift.pay_rate) || 0;
                employeeAggregates[emp.id].totalNetHours += hours;
                employeeAggregates[emp.id].totalEarnings += (hours * rate);
              });
            }
          });
        }
      });

      const employeesArray = Object.values(employeeAggregates);
      setAllEmployees(employeesArray);

      // 4. Find Winners
      let maxHours = -1;
      let maxMoney = -1;
      let winnerHours = null;
      let winnerMoney = null;

      employeesArray.forEach((emp) => {
        if (emp.totalNetHours > maxHours) {
          maxHours = emp.totalNetHours;
          winnerHours = emp;
        }
        if (emp.totalEarnings > maxMoney) {
          maxMoney = emp.totalEarnings;
          winnerMoney = emp;
        }
      });

      setTopHoursEmployee(winnerHours);
      setTopEarningEmployee(winnerMoney);

    } catch (error) {
      console.error("Error fetching labor stats:", error);
    }
  };

  // --- ACTIONS & HELPERS ---

  const loadReports = async (start, end) => {
    setLoading(true);
    setProgressMessages([]);
    try {
      const reportsObj = await loadReportsHelper({
        token: user.token,
        start,
        end,
        included: included.includes("all") ? ["all"] : included,
        selected: [{ value: "All Locations" }],
        setProgressMessages,
        setLocations: () => { },
        setActiveTab: () => { },
      });
      setReports(reportsObj || {});

      // Automatically fetch top employees based on the loaded location data
      setProgressMessages(prev => [...prev, "Analyzing employee performance..."]);
      await fetchAndCalculateTopEmployees(reportsObj, start, end);
    } finally {
      setLoading(false);
      setProgressMessages([]);
    }
  };

  // Initial Load
  useEffect(() => {
    loadReports(startDate, endDate);
    // eslint-disable-next-line
  }, []);

  const toggleIncluded = (key) => {
    setIncluded((prev) => {
      if (key === "all") return ["all"];
      const withoutAll = prev.filter((i) => i !== "all");
      if (withoutAll.includes(key)) {
        return withoutAll.filter((i) => i !== key);
      }
      return [...withoutAll, key];
    });
  };

  const accountManagers = useMemo(() => {
    const managers = new Set();
    Object.values(reports).forEach((platforms) => {
      if (platforms.account_manager) {
        managers.add(platforms.account_manager);
      }
    });
    return ["All Managers", ...Array.from(managers)];
  }, [reports]);

  return (
    <React.Fragment>
      <div
        className="page-content"
        style={{ marginTop: -70, backgroundColor: "rgb(38, 37, 35)" }}
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xl={12}>
              <PerformanceSummaryView
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedManager={selectedManager}
                setSelectedManager={setSelectedManager}
                selectedStructure={selectedStructure}
                setSelectedStructure={setSelectedStructure}
                included={included}
                toggleIncluded={toggleIncluded}
                filterBySource={filterBySource}
                setFilterBySource={setFilterBySource}
                loadReports={loadReports}
                loading={loading}
                accountManagers={accountManagers}
                progressMessages={progressMessages}
              />
            </Col>
            <Col xl={12}>
              <LocationsPerformanceTable
                reports={reports}
                selectedManager={selectedManager}
                selectedStructure={selectedStructure}
                included={included}
                filterBySource={filterBySource}
              />
            </Col>
            <Col xl={12} className="mt-4">
              <LeaseSettlementsPerformanceTable
                reports={reports}
                selectedManager={selectedManager}
                selectedStructure={selectedStructure}
              />
            </Col>
            <Col xl={6} className="d-flex align-items-stretch justify-content-center">
              {topHoursEmployee && (
                <TopEmployeeCard
                  title="Most Hours Worked"
                  employee={topHoursEmployee}
                  type="hours"
                />
              )}
            </Col>
            <Col xl={6} className="d-flex align-items-stretch justify-content-center">
              {topEarningEmployee && (
                <TopEmployeeCard
                  title="Highest Earner"
                  employee={topEarningEmployee}
                  type="earnings"
                />
              )}
            </Col>
            <Col xl={12} className="mt-4">
              <EmployeesPerformanceTable employees={allEmployees} />
            </Col>
            <Col xl={12} className="mt-4">
              <MonthlyPerformanceChart
                user={user}
                included={included}
                selectedManager={selectedManager}
                selectedStructure={selectedStructure}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Performance.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
};

const TopEmployeeCard = ({ title, employee, type }) => {
  if (!employee) return null;

  // Determine values based on type
  const valueDisplay = type === "hours"
    ? `${employee.totalNetHours.toFixed(2)} Hrs`
    : `$${formatCurrency(employee.totalEarnings)}`;

  const subLabel = type === "hours" ? "Total Hours Worked" : "Estimated Earnings";

  // Avatar logic
  const avatarUrl = employee.picture
    ? `${FILES_URL}${employee.picture}`
    : null;

  // Determine W2 vs 1099 based on first shift found (simple logic)
  const paymentType = employee.shifts && employee.shifts[0]?.payment_type === "1099" ? "1099" : "W2";

  // Calculate effective average rate for the earnings card
  const effectiveRate = employee.totalNetHours > 0
    ? (employee.totalEarnings / employee.totalNetHours).toFixed(2)
    : "0.00";

  return (
    <div
      style={{
        backgroundColor: "#2f2f2f",
        borderRadius: "14px",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        border: "1px solid rgba(255,255,255,0.1)",
        height: "100%",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)"
      }}
    >
      <h5 style={{ color: "#FFCA5C", marginBottom: "20px", fontWeight: "bold" }}>{title}</h5>

      <div
        style={{
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          border: "3px solid #FFCA5C",
          backgroundColor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "15px",
          overflow: "hidden",
        }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <i className="bx bxs-user" style={{ fontSize: "40px", color: "#fff" }}></i>
        )}
      </div>

      <h4 style={{ color: "#fff", fontWeight: "bold", marginBottom: "5px" }}>
        {employee.full_name || employee.employee_alias}
      </h4>

      <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
        {/* Payment Type Badge */}
        <span className="badge rounded-pill bg-dark" style={{ border: "1px solid #555", color: "#ccc", width: "fit-content" }}>
          {paymentType}
        </span>

        {/* Location Names Badge */}
        <span
          className="badge rounded-pill"
          style={{
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#93C5FD",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            fontWeight: "600",
            whiteSpace: "normal",
            maxWidth: "280px",
            lineHeight: "1.4"
          }}
        >
          {employee.locationNames ? Array.from(employee.locationNames).join(", ") : ""}
        </span>

        {/* Hours & Rate Badge (Only for Earnings Winner) */}
        {type === "earnings" && (
          <span
            className="badge rounded-pill"
            style={{
              backgroundColor: "rgba(34, 197, 94, 0.15)", // Green tint for money stats
              color: "#4ade80",
              border: "1px solid rgba(34, 197, 94, 0.3)",
              fontWeight: "600",
              fontSize: "11px",
              marginTop: "2px"
            }}
          >
            {employee.totalNetHours.toFixed(1)} Hrs @ ${effectiveRate}/hr
          </span>
        )}
      </div>

      <div style={{ marginTop: "auto" }}>
        <div style={{ color: "#FFCA5C", fontSize: "28px", fontWeight: "800" }}>
          {valueDisplay}
        </div>
        <div style={{ color: "#888", fontSize: "13px", textTransform: "uppercase", letterSpacing: "1px" }}>
          {subLabel}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(Performance);